import React from 'react';
import { BgImage } from '../components/Img';
import { IMG } from '../images';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Award, AlertCircle } from 'lucide-react';

const SafetyCompliance = () => {
  const standards = [
    {
      title: 'AS/NZS 4488',
      subtitle: 'Industrial Rope Access Systems',
      description: 'Comprehensive standard for rope access work, covering training, equipment, and safe working procedures'
    },
    {
      title: 'AS/NZS 1891',
      subtitle: 'Industrial Fall Arrest Systems',
      description: 'Standards for fall arrest systems and equipment including harnesses, lanyards, and anchor points'
    },
    {
      title: 'WHS Regulations',
      subtitle: 'Work Health and Safety',
      description: 'Compliance with all NSW Work Health and Safety regulations and requirements'
    }
  ];

  const safetyPractices = [
    'Detailed risk assessments before every project',
    'Safe Work Method Statements (SWMS) for all operations',
    'Regular equipment inspections and maintenance',
    'Anchor point verification and certification',
    'Emergency response procedures',
    'Continuous technician training and certification'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section 
        className="hero" 
        style={{
          minHeight: '60vh'
        }}
      >
        <BgImage image={IMG.hero} position="60% 50%" priority />
        <div className="hero-content">
          <Shield size={64} style={{ margin: '0 auto 1rem', color: 'var(--safety-orange)' }} />
          <h1>Safety & Compliance</h1>
          <p>Our commitment to the highest safety standards in rope access operations</p>
        </div>
      </section>

      {/* Intro */}
      <section className="section">
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: '2.5rem', color: 'var(--navy-blue)', marginBottom: '1.5rem' }}>
              Safety is the Foundation of Everything We Do
            </h2>
            <p style={{ fontSize: '1.2rem', lineHeight: 1.8, color: 'var(--steel-gray)' }}>
              At ProClimb Solutions, safety is not just a priority—it's the core of our operations. All rope access works are carried out in strict accordance with relevant Australian Standards and industry best practices.
            </p>
          </div>
        </div>
      </section>

      {/* Standards */}
      <section className="section bg-light">
        <div className="container">
          <div className="section-header">
            <h2>Australian Standards Compliance</h2>
            <p>We operate under recognised standards to ensure maximum safety and quality</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {standards.map((standard, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--white)',
                  padding: '2rem',
                  border: '1px solid var(--border-color)',
                  borderLeft: '4px solid var(--safety-orange)'
                }}
              >
                <Award size={40} style={{ color: 'var(--navy-blue)', marginBottom: '1rem' }} />
                <h3 style={{ color: 'var(--navy-blue)', fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                  {standard.title}
                </h3>
                <p style={{ color: 'var(--safety-orange)', fontWeight: 600, marginBottom: '1rem' }}>
                  {standard.subtitle}
                </p>
                <p style={{ color: 'var(--steel-gray)', lineHeight: 1.7 }}>
                  {standard.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certified Technicians */}
      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div>
              <h2 style={{ color: 'var(--navy-blue)', fontSize: '2.5rem', marginBottom: '1.5rem' }}>
                Certified Rope Access Technicians
              </h2>
              <p style={{ fontSize: '1.1rem', lineHeight: 1.8, marginBottom: '1.5rem' }}>
                Our technicians are IRATA Level 3 certified and trained to perform complex rope access operations on high-rise structures, façades and industrial environments.
              </p>
              <p style={{ fontSize: '1.1rem', lineHeight: 1.8, marginBottom: '1.5rem' }}>
                Every member of our team undergoes rigorous training and maintains current certifications to ensure they can safely execute even the most challenging rope access projects.
              </p>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <Link to="/contact" className="btn-primary">Request Quote</Link>
                <Link to="/about" className="btn-secondary" style={{ border: '2px solid var(--industrial-black)', color: 'var(--industrial-black)' }}>
                  About Our Team
                </Link>
              </div>
            </div>
            <div style={{ background: 'var(--light-gray)', padding: '2rem', border: '1px solid var(--border-color)' }}>
              <h3 style={{ color: 'var(--navy-blue)', marginBottom: '1.5rem', fontSize: '1.5rem' }}>Certifications Include:</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'IRATA Level 3 Certification',
                  'Working at Heights Certification',
                  'First Aid & CPR Certified',
                  'Confined Space Entry',
                  'Rigging & Height Safety',
                  'Rescue Procedures Training'
                ].map((cert, index) => (
                  <li key={index} style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
                    <CheckCircle size={20} style={{ color: 'var(--safety-orange)', flexShrink: 0 }} />
                    <span style={{ fontSize: '1.05rem' }}>{cert}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Assessment */}
      <section className="section bg-dark">
        <div className="container">
          <div className="section-header">
            <h2 style={{ color: 'var(--white)' }}>Risk Assessment & Safe Work Method Statements</h2>
            <p style={{ color: '#e5e7eb' }}>Comprehensive planning before every project</p>
          </div>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <p style={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#e5e7eb', marginBottom: '2rem', textAlign: 'center' }}>
              Before commencing any work, we perform detailed assessments to identify and mitigate all potential risks.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
              {safetyPractices.map((practice, index) => (
                <div 
                  key={index}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    padding: '1.5rem',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'flex-start'
                  }}
                >
                  <CheckCircle size={24} style={{ color: 'var(--safety-orange)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--white)', fontSize: '1.05rem' }}>{practice}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Equipment */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Equipment Standards</h2>
            <p>All equipment maintained to the highest safety specifications</p>
          </div>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ background: 'var(--light-gray)', padding: '2.5rem', border: '1px solid var(--border-color)' }}>
              <h3 style={{ color: 'var(--navy-blue)', marginBottom: '1.5rem', fontSize: '1.8rem' }}>
                Our Commitment to Equipment Safety
              </h3>
              <p style={{ fontSize: '1.1rem', lineHeight: 1.8, marginBottom: '1.5rem' }}>
                All rope access equipment is regularly inspected, maintained according to manufacturer specifications, and compliant with Australian safety standards.
              </p>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Pre-use inspection of all equipment before every job',
                  'Regular maintenance schedules and records',
                  'Immediate replacement of worn or damaged equipment',
                  'Compliance with AS/NZS standards for all gear',
                  'Anchor point certification and verification',
                  'Complete equipment traceability and documentation'
                ].map((item, index) => (
                  <li key={index} style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'flex-start' }}>
                    <CheckCircle size={20} style={{ color: 'var(--safety-orange)', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '1.05rem', lineHeight: 1.7 }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance */}
      <section className="section bg-light">
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <Shield size={64} style={{ color: 'var(--safety-orange)', margin: '0 auto 2rem' }} />
            <h2 style={{ color: 'var(--navy-blue)', fontSize: '2.5rem', marginBottom: '1.5rem' }}>
              Fully Insured for Your Peace of Mind
            </h2>
            <p style={{ fontSize: '1.1rem', lineHeight: 1.8, marginBottom: '2rem' }}>
              ProClimb Solutions maintains comprehensive insurance coverage including Public Liability Insurance, Workers Compensation, and Professional Indemnity where required.
            </p>
            <p style={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Insurance certificates and compliance documentation can be provided upon request for all projects.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ marginBottom: '1.5rem' }}>Work With a Safety-First Team</h2>
          <p style={{ fontSize: '1.2rem', color: 'var(--steel-gray)', marginBottom: '2rem' }}>
            Contact us to discuss your rope access project and our safety protocols
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary">Get a Quote</Link>
            <a href="tel:0426920301" className="btn-secondary" style={{ border: '2px solid var(--industrial-black)', color: 'var(--industrial-black)' }}>
              Call: 0426 920 301
            </a>
          </div>
        </div>
      </section>

      {/* Responsive */}
      <style jsx>{`
        @media (max-width: 768px) {
          div[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SafetyCompliance;
